import { Component } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled/macro';
import { NavWrapper, NavInnerWrapper } from './base';
import NavSearch from './NavSearch';
import NavLinksCreate from './NavLinksCreate';
import NavLinksCollection from './NavLinksCollection';
import NavLinksUser from './NavLinksUser';
import customIcons from '../styles/icons';
import { fonts } from '../styles/theme';
import { navHeight, navItemColor, navItemHoverColor } from './theme-nav';

const NavTouchIcon = styled('div')`
  display: none;
  padding: 0 20px;
  text-align: center;
  position: absolute;
  left: 0px;
  &::after {
    ${fonts.icons};
    content: '${({ mobilemenu }) => (mobilemenu ? customIcons.x : customIcons['touch-nav'])}';
    color: ${navItemColor};
    line-height: ${navHeight};
    font-size: ${fonts.sans.size.xlarge};
  }
  &:hover {
    cursor: pointer;
    &::after {
      color: ${navItemHoverColor};
    }
  }
  @media screen and (max-width: 1000px) {
    display: block;
    & + ${NavInnerWrapper} {
      right: ${({ mobilemenu }) => (mobilemenu ? 0 : '-220px')};
    }
  }
`;

class NavTouch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mobilemenu: props.mobilemenu || false,
    };
  }

  toggleMenu = () => {
    const { mobilemenu } = this.state;
    const toggleState = !mobilemenu;

    this.setState({
      mobilemenu: toggleState,
    });
  };

  render() {
    const { ...otherProps } = this.state;
    return <NavTouchIcon onClick={this.toggleMenu} {...otherProps} />;
  }
}

NavTouch.defaultProps = {
  mobilemenu: false,
  onChange: () => {},
};

NavTouch.propTypes = {
  mobilemenu: PropTypes.bool,
  onChange: PropTypes.func,
};

const Nav = (props) => {
  const {
    basic,
    align,
    theme,
    onSearch,
    searchPlaceholder,
    ready,
    avatarUrl,
    data: { primaryLinks, createLinks, secondaryLinks, userLinks },
  } = props;

  const isEmpty = (arr) => Array.isArray(arr) && arr.every(isEmpty);

  const createLinksExist = () => !isEmpty(createLinks);

  return (
    <ThemeProvider theme={{ mode: theme }}>
      <NavWrapper ready={ready}>
        <NavTouch />
        <NavInnerWrapper align={align}>
          {basic ? (
            <NavLinksCollection links={secondaryLinks} />
          ) : (
            <>
              <NavLinksCollection links={primaryLinks} mobileorder="4" />
              <NavSearch onSearch={onSearch} placeholder={searchPlaceholder} mobileorder="1" />
              {createLinksExist(createLinks) && (
                <NavLinksCreate links={createLinks} mobileorder="3" />
              )}
              <NavLinksCollection links={secondaryLinks} mobileorder="4" />
              <NavLinksUser links={userLinks} avatarUrl={avatarUrl} mobileorder="1" />
            </>
          )}
        </NavInnerWrapper>
      </NavWrapper>
    </ThemeProvider>
  );
};

const linkShape = PropTypes.shape({
  name: PropTypes.string,
  path: PropTypes.string,
  children: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        name: PropTypes.string,
        path: PropTypes.string,
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          path: PropTypes.string,
        })
      ),
    ])
  ),
});

const linkCollectionShape = PropTypes.arrayOf(
  PropTypes.oneOfType([linkShape, PropTypes.arrayOf(linkShape)])
);

Nav.defaultProps = {
  basic: false,
  align: 'left',
  theme: 'default',
  ready: true,
  avatarUrl: undefined,
  onSearch: null,
  searchPlaceholder: '',
};

Nav.propTypes = {
  basic: PropTypes.bool,
  align: PropTypes.string,
  theme: PropTypes.string,
  ready: PropTypes.bool,
  onSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  searchPlaceholder: PropTypes.string,
  data: PropTypes.shape({
    primaryLinks: linkCollectionShape.isRequired,
    createLinks: linkCollectionShape.isRequired,
    secondaryLinks: linkCollectionShape.isRequired,
    userLinks: linkCollectionShape.isRequired,
  }).isRequired,
  avatarUrl: PropTypes.string,
};

export default Nav;
